import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import posthog from 'posthog-js';
import DraggableBar from '../../components/DraggableBar';
import ProgressBar from '../../components/ProgressBar';

const BLUE    = 'var(--signal)';
const TEXT1   = 'var(--ink)';
const TEXT2   = 'var(--slate)';
const TEXT3   = 'var(--mute)';
const MONTH_INITIALS = ['J','F','M','A','M','J','J','A','S','O','N','D'];
const TRADE_TYPES = [
  'PDR Technician','Roofing Contractor','HVAC Technician',
  'Electrician','Plumber','Other',
];
function fmtStat(n) {
  if (n >= 10000) return '$' + Math.round(n / 1000) + 'k';
  return '$' + Math.round(n).toLocaleString();
}

function fmtBar(n) {
  if (n >= 10000) return '$' + Math.round(n / 1000) + 'K';
  if (n >= 1000)  return '$' + (n / 1000).toFixed(1) + 'K';
  return '$' + Math.round(n);
}

function StickyHeader() {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      height: 56, backgroundColor: 'var(--card)',
      borderBottom: '0.5px solid rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'center',
      padding: '0 20px',
    }}>
      <span style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.3px' }}>
        <span style={{ color: TEXT1 }}>Tru</span>
        <span style={{ color: BLUE }}>Flow</span>
      </span>
    </div>
  );
}

function FocusSelect({ value, onChange, children }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      value={value}
      onChange={onChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: '100%', height: 44,
        border: `0.5px solid ${focused ? BLUE : 'rgba(0,0,0,0.18)'}`,
        borderRadius: 8, padding: '0 12px',
        fontSize: 14, color: TEXT1,
        backgroundColor: 'var(--card)', outline: 'none',
        boxSizing: 'border-box',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        cursor: 'pointer',
      }}
    >
      {children}
    </select>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid var(--signal-soft)', borderTopColor: BLUE, animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function IncomeCurve() {
  const navigate = useNavigate();
  // Signup no longer asks for occupation, so this default is a deliberate call
  // on who the current user base is rather than a value carried in from
  // anywhere. The ref pins whatever the dropdown starts on so the mount-effect
  // below seeds the curve from that same value — keying the seed off its own
  // literal is how the chart and the dropdown drifted apart before. It stays a
  // ref, not a dep, because handleTradeChange already reseeds on every change
  // and the effect must not re-fetch.
  const [tradeType, setTradeType]       = useState('PDR Technician');
  const initialTrade                    = useRef(tradeType);
  const [weights, setWeights]           = useState(Array(12).fill(8));
  const [defaults, setDefaults]         = useState({});
  const [annualIncome, setAnnualIncome] = useState(80000);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');

  useEffect(() => {
    let cancelled = false;
    async function init() {
      const [defaultsRes, taxRes] = await Promise.allSettled([
        api.get('/income/defaults'),
        api.get('/tax/setup'),
      ]);

      if (cancelled) return;
      if (defaultsRes.status === 'fulfilled') {
        const d = defaultsRes.value.data;
        setDefaults(d);
        const key = initialTrade.current;
        if (d[key]) setWeights([...d[key]]);
      }
      if (taxRes.status === 'fulfilled') {
        const income = taxRes.value.data?.annual_income;
        if (income) setAnnualIncome(income);
      }
      setLoading(false);
    }
    init();
    return () => { cancelled = true; };
  }, []);

  function handleTradeChange(e) {
    const trade = e.target.value;
    setTradeType(trade);
    if (defaults[trade]) setWeights([...defaults[trade]]);
  }

  function updateWeight(index, newWeight) {
    setWeights((prev) => {
      const next = [...prev];
      next[index] = newWeight;
      return next;
    });
  }

  function handleReset() {
    if (defaults[tradeType]) setWeights([...defaults[tradeType]]);
  }
async function handleSave() {
  setSaving(true);
  setError('');

  const totalWeight = weights.reduce((a, b) => a + b, 0);

  if (totalWeight === 0) {
    setError('Please set at least one month with income before saving.');
    setSaving(false);
    return;
  }

  const nonZeroWeights = weights.filter(w => w > 0);
  const safeSlowAmt = nonZeroWeights.length > 0
    ? Math.round((Math.min(...nonZeroWeights) / totalWeight) * annualIncome)
    : 1;
  const safeStrongAmt = Math.round((Math.max(...weights) / totalWeight) * annualIncome);

  const roundedWeights = weights.map(w => Math.round(w * 100) / 100);

  try {
    await api.post('/income/curve', {
      month_weights: roundedWeights,
      annual_income: annualIncome,
      strong_month_amount: Math.max(2, safeStrongAmt),
      slow_month_amount: Math.max(1, safeSlowAmt),
      trade_type: tradeType,
    });
    posthog.capture('income_curve_completed');
    navigate('/onboarding/expenses');
  } catch (err) {
    setError(err.response?.data?.detail || 'Could not save. Please try again.');
  } finally {
    setSaving(false);
  }
}

  const totalW   = weights.reduce((a, b) => a + b, 0);
  const maxW     = Math.max(...weights);
  const minW     = Math.min(...weights);
  const strongAmt = totalW > 0 ? (maxW / totalW) * annualIncome : 0;
  const slowAmt   = totalW > 0 ? (minW / totalW) * annualIncome : 0;

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
        <StickyHeader />
        <ProgressBar pct={75} />
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
      <StickyHeader />
      <ProgressBar pct={75} />

      <div style={{ maxWidth: 480, margin: '0 auto', padding: '20px 16px 40px' }}>
        <p style={{ margin: '0 0 4px', fontSize: 11, color: TEXT3 }}>Step 3 of 4</p>
        <h1 style={{ margin: '0 0 8px', fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: TEXT1 }}>Your income pattern</h1>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: TEXT2, lineHeight: 1.5 }}>
          Adjust to match how you actually earn across the year.
        </p>

        <div style={{ marginBottom: 20 }}>
          <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: TEXT1 }}>I work as a...</p>
          <FocusSelect value={tradeType} onChange={handleTradeChange}>
            {TRADE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </FocusSelect>
        </div>

        <div style={{
          backgroundColor: 'var(--card)',
          boxShadow: 'var(--sh-card)',
          borderRadius: 'var(--r-card)', padding: 16, marginBottom: 14,
        }}>
          <div style={{ display: 'flex' }}>
            {weights.map((w, i) => (
              <DraggableBar key={i} index={i} weight={w} onWeightChange={updateWeight} />
            ))}
          </div>

          <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
            {MONTH_INITIALS.map((m, i) => {
              const monthAmt = totalW > 0 ? (weights[i] / totalW) * annualIncome : 0;
              return (
                <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 8, color: TEXT3 }}>{m}</div>
                  <div style={{ fontSize: 7, color: TEXT2, marginTop: 1 }}>{fmtBar(monthAmt)}</div>
                </div>
              );
            })}
          </div>

          <div style={{
            display: 'flex', backgroundColor: 'var(--paper)',
            borderRadius: 8, padding: '8px 0', marginTop: 14,
          }}>
            {[
              { label: 'Strong month', value: fmtStat(strongAmt) },
              { label: 'Annual total',  value: fmtStat(annualIncome) },
              { label: 'Slow month',    value: fmtStat(slowAmt) },
            ].map(({ label, value }, i) => (
              <div key={i} style={{
                flex: 1, textAlign: 'center',
                borderRight: i < 2 ? '0.5px solid rgba(0,0,0,0.1)' : 'none',
                padding: '0 4px',
              }}>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: TEXT1, fontFamily: 'var(--font-serif)' }}>{value}</p>
                <p style={{ margin: '2px 0 0', fontSize: 10, color: TEXT3 }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {error && (
          <p style={{
            color: 'var(--stop)', fontSize: 13,
            margin: '0 0 12px', padding: '10px 12px',
            backgroundColor: 'var(--stop-soft)', border: '1px solid var(--stop-border)',
            borderRadius: 8,
          }}>
            {error}
          </p>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%',
            backgroundColor: saving ? 'var(--signal-disabled)' : BLUE,
            color: 'var(--on-signal)', borderRadius: 10,
            padding: 13, fontSize: 14, fontWeight: 500,
            border: 'none', cursor: saving ? 'default' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save my income pattern →'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button
            onClick={handleReset}
            style={{
              background: 'none', border: 'none',
              color: TEXT3, fontSize: 12,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Reset to default
          </button>
        </div>
      </div>
    </div>
  );
}
